import {
  projectCanonicalTreeCommandCenterFeedHistoricalAuthorityPresentation,
  type CanonicalTreeCommandCenterFeedHistoricalAuthorityPresentation,
} from './CanonicalTreeCommandCenterFeedHistoricalAuthorityPresentationProjection';

export type CanonicalTreeHistoricalAuthorityPresentationContainmentReason =
  | 'NONE'
  | 'PROJECTION_OR_FEED_FAILURE'
  | 'NULL_OR_UNDEFINED_PROJECTION'
  | 'MALFORMED_PROJECTION';

export interface CanonicalTreeHistoricalAuthorityUnavailablePresentation {
  readonly milestone:
    'CC-TREE-ARCH-076';

  readonly presentation:
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FAILURE_CONTAINMENT';

  readonly presentationState:
    'HISTORICAL_AUTHORITY_PRESENTATION_UNAVAILABLE';

  readonly tone:
    'neutral';

  readonly label:
    'Historical authority presentation unavailable';

  readonly detail:
    'Historical repository-authority evidence could not be presented safely. Other Tree workspace surfaces remain available.';

  readonly sourceFeedGeneratedAt:
    'Unavailable';

  readonly sourceReadModelMilestone:
    'CC-TREE-ARCH-068';

  readonly currentRepositoryAuthorityMilestone:
    'CC-TREE-ARCH-066';

  readonly consolidationAuthorityMilestone:
    'CC-TREE-ARCH-067';

  readonly currentPhysicalSourceState:
    'FOUR_RETIRED_SOURCES_ABSENT';

  readonly registryOnlyProductionAuthority:
    false;

  readonly immutableHistoricalTruthPreserved:
    false;

  readonly immutableArchiveAuthority:
    false;

  readonly historicalVerifierAuthorityPreserved:
    false;

  readonly historicalDocumentationAuthorityPreserved:
    false;

  readonly physicalRepositorySourceCount:
    0;

  readonly deletedHistoricalSourceCount:
    0;

  readonly activeProductionReferenceCount:
    0;

  readonly historicalAuthorityCount:
    0;

  readonly currentAuthorityCount:
    0;

  readonly milestoneCount:
    0;

  readonly arch056ArchiveChanged:
    false;

  readonly arch062ArchiveChanged:
    false;

  readonly certifiesTreeHealth:
    false;

  readonly certifiesReleaseReadiness:
    false;

  readonly certifiesDeploymentReadiness:
    false;

  readonly affectsRuntimeDecision:
    false;

  readonly affectsRuntimeGuard:
    false;

  readonly affectsOperationalCertification:
    false;

  readonly memberFacing:
    false;

  readonly workspaceWiringCompleted:
    true;

  readonly nextGovernedDependency:
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FAILURE_CONTAINMENT_CERTIFICATION';
}

export type CanonicalTreeContainedHistoricalAuthorityPresentation =
  | CanonicalTreeCommandCenterFeedHistoricalAuthorityPresentation
  | CanonicalTreeHistoricalAuthorityUnavailablePresentation;

export interface CanonicalTreeHistoricalAuthorityPresentationValidation {
  readonly valid:
    boolean;

  readonly reason:
    CanonicalTreeHistoricalAuthorityPresentationContainmentReason;
}

export interface CanonicalTreeHistoricalAuthorityPresentationContainmentResult {
  readonly status:
    'AVAILABLE' | 'FALLBACK';

  readonly reason:
    CanonicalTreeHistoricalAuthorityPresentationContainmentReason;

  readonly presentation:
    CanonicalTreeContainedHistoricalAuthorityPresentation;

  readonly failureMessage:
    string | null;
}

export interface CanonicalTreeHistoricalAuthorityPresentationFailureContainmentImplementation {
  readonly milestone:
    'CC-TREE-ARCH-076';

  readonly authority:
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FAILURE_CONTAINMENT_IMPLEMENTATION';

  readonly decision:
    'LOCAL_TREE_HISTORICAL_AUTHORITY_PRESENTATION_FAILURE_CONTAINMENT_IMPLEMENTED';

  readonly auditMilestone:
    'CC-TREE-ARCH-075';

  readonly guardedProjectionResolverImplemented:
    true;

  readonly deterministicValidationImplemented:
    true;

  readonly immutableNeutralFallbackImplemented:
    true;

  readonly localReactErrorBoundaryImplemented:
    true;

  readonly workspaceSiblingSurfacesPreserved:
    true;

  readonly commandCenterSafePanelReused:
    false;

  readonly runtimeDecisionDependencyAdded:
    false;

  readonly runtimeGuardDependencyAdded:
    false;

  readonly operationalCertificationDependencyAdded:
    false;

  readonly memberFacingStateAdded:
    false;

  readonly certifiesTreeHealth:
    false;

  readonly certifiesReleaseReadiness:
    false;

  readonly certifiesDeploymentReadiness:
    false;

  readonly containmentImplementationCompleted:
    true;

  readonly nextGovernedDependency:
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FAILURE_CONTAINMENT_CERTIFICATION';
}

const validPresentationStates =
  new Set([
    'REGISTRY_ONLY_AUTHORITY_CONFIRMED',
    'PHYSICAL_SOURCE_DELETION_CONFIRMED',
    'HISTORICAL_AUTHORITY_AVAILABLE',
    'HISTORICAL_AUTHORITY_INCONSISTENT',
  ]);

const validTones =
  new Set([
    'confirmed',
    'informational',
    'warning',
  ]);

const requiredStringFields = [
  'milestone',
  'presentation',
  'presentationState',
  'tone',
  'label',
  'detail',
  'sourceFeedGeneratedAt',
  'sourceReadModelMilestone',
  'currentRepositoryAuthorityMilestone',
  'consolidationAuthorityMilestone',
  'currentPhysicalSourceState',
  'nextGovernedDependency',
] as const;

const requiredNumericFields = [
  'physicalRepositorySourceCount',
  'deletedHistoricalSourceCount',
  'activeProductionReferenceCount',
  'historicalAuthorityCount',
  'currentAuthorityCount',
  'milestoneCount',
] as const;

const requiredBooleanFields = [
  'registryOnlyProductionAuthority',
  'immutableHistoricalTruthPreserved',
  'immutableArchiveAuthority',
  'historicalVerifierAuthorityPreserved',
  'historicalDocumentationAuthorityPreserved',
  'arch056ArchiveChanged',
  'arch062ArchiveChanged',
  'certifiesTreeHealth',
  'certifiesReleaseReadiness',
  'certifiesDeploymentReadiness',
  'affectsRuntimeDecision',
  'affectsRuntimeGuard',
  'affectsOperationalCertification',
  'memberFacing',
  'workspaceWiringCompleted',
] as const;

const isRecord = (
  value:
    unknown,
): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value);

export const CANONICAL_TREE_HISTORICAL_AUTHORITY_PRESENTATION_UNAVAILABLE:
CanonicalTreeHistoricalAuthorityUnavailablePresentation =
  Object.freeze({
    milestone:
      'CC-TREE-ARCH-076',

    presentation:
      'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FAILURE_CONTAINMENT',

    presentationState:
      'HISTORICAL_AUTHORITY_PRESENTATION_UNAVAILABLE',

    tone:
      'neutral',

    label:
      'Historical authority presentation unavailable',

    detail:
      'Historical repository-authority evidence could not be presented safely. Other Tree workspace surfaces remain available.',

    sourceFeedGeneratedAt:
      'Unavailable',

    sourceReadModelMilestone:
      'CC-TREE-ARCH-068',

    currentRepositoryAuthorityMilestone:
      'CC-TREE-ARCH-066',

    consolidationAuthorityMilestone:
      'CC-TREE-ARCH-067',

    currentPhysicalSourceState:
      'FOUR_RETIRED_SOURCES_ABSENT',

    registryOnlyProductionAuthority:
      false,

    immutableHistoricalTruthPreserved:
      false,

    immutableArchiveAuthority:
      false,

    historicalVerifierAuthorityPreserved:
      false,

    historicalDocumentationAuthorityPreserved:
      false,

    physicalRepositorySourceCount:
      0,

    deletedHistoricalSourceCount:
      0,

    activeProductionReferenceCount:
      0,

    historicalAuthorityCount:
      0,

    currentAuthorityCount:
      0,

    milestoneCount:
      0,

    arch056ArchiveChanged:
      false,

    arch062ArchiveChanged:
      false,

    certifiesTreeHealth:
      false,

    certifiesReleaseReadiness:
      false,

    certifiesDeploymentReadiness:
      false,

    affectsRuntimeDecision:
      false,

    affectsRuntimeGuard:
      false,

    affectsOperationalCertification:
      false,

    memberFacing:
      false,

    workspaceWiringCompleted:
      true,

    nextGovernedDependency:
      'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FAILURE_CONTAINMENT_CERTIFICATION',
  });

export const validateCanonicalTreeHistoricalAuthorityPresentation =
  (
    value:
      unknown,
  ): CanonicalTreeHistoricalAuthorityPresentationValidation => {
    if (!isRecord(value)) {
      return Object.freeze({
        valid:
          false,

        reason:
          value == null
            ? 'NULL_OR_UNDEFINED_PROJECTION'
            : 'MALFORMED_PROJECTION',
      });
    }

    for (const field of requiredStringFields) {
      const fieldValue =
        value[field];

      if (
        typeof fieldValue !== 'string' ||
        fieldValue.trim().length === 0
      ) {
        return Object.freeze({
          valid:
            false,

          reason:
            'MALFORMED_PROJECTION',
        });
      }
    }

    if (
      !validPresentationStates.has(
        value.presentationState as string,
      ) ||
      !validTones.has(
        value.tone as string,
      )
    ) {
      return Object.freeze({
        valid:
          false,

        reason:
          'MALFORMED_PROJECTION',
      });
    }

    if (
      Number.isNaN(
        Date.parse(
          value.sourceFeedGeneratedAt as string,
        ),
      )
    ) {
      return Object.freeze({
        valid:
          false,

        reason:
          'MALFORMED_PROJECTION',
      });
    }

    for (const field of requiredNumericFields) {
      const fieldValue =
        value[field];

      if (
        typeof fieldValue !== 'number' ||
        !Number.isFinite(fieldValue) ||
        fieldValue < 0
      ) {
        return Object.freeze({
          valid:
            false,

          reason:
            'MALFORMED_PROJECTION',
        });
      }
    }

    for (const field of requiredBooleanFields) {
      if (
        typeof value[field] !== 'boolean'
      ) {
        return Object.freeze({
          valid:
            false,

          reason:
            'MALFORMED_PROJECTION',
        });
      }
    }

    if (
      value.certifiesTreeHealth !== false ||
      value.certifiesReleaseReadiness !== false ||
      value.certifiesDeploymentReadiness !== false ||
      value.affectsRuntimeDecision !== false ||
      value.affectsRuntimeGuard !== false ||
      value.affectsOperationalCertification !== false ||
      value.memberFacing !== false
    ) {
      return Object.freeze({
        valid:
          false,

        reason:
          'MALFORMED_PROJECTION',
      });
    }

    return Object.freeze({
      valid:
        true,

      reason:
        'NONE',
    });
  };

export const resolveContainedCanonicalTreeHistoricalAuthorityPresentation =
  (
    projector:
      () => unknown =
        projectCanonicalTreeCommandCenterFeedHistoricalAuthorityPresentation,
  ): CanonicalTreeHistoricalAuthorityPresentationContainmentResult => {
    try {
      const projected =
        projector();

      const validation =
        validateCanonicalTreeHistoricalAuthorityPresentation(
          projected,
        );

      if (!validation.valid) {
        return Object.freeze({
          status:
            'FALLBACK',

          reason:
            validation.reason,

          presentation:
            CANONICAL_TREE_HISTORICAL_AUTHORITY_PRESENTATION_UNAVAILABLE,

          failureMessage:
            null,
        });
      }

      return Object.freeze({
        status:
          'AVAILABLE',

        reason:
          'NONE',

        presentation:
          projected as CanonicalTreeCommandCenterFeedHistoricalAuthorityPresentation,

        failureMessage:
          null,
      });
    } catch (error) {
      return Object.freeze({
        status:
          'FALLBACK',

        reason:
          'PROJECTION_OR_FEED_FAILURE',

        presentation:
          CANONICAL_TREE_HISTORICAL_AUTHORITY_PRESENTATION_UNAVAILABLE,

        failureMessage:
          error instanceof Error
            ? error.message
            : String(error),
      });
    }
  };

export const resolveCanonicalTreeHistoricalAuthorityPresentationFailureContainmentImplementation =
  (): CanonicalTreeHistoricalAuthorityPresentationFailureContainmentImplementation =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-076',

      authority:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FAILURE_CONTAINMENT_IMPLEMENTATION',

      decision:
        'LOCAL_TREE_HISTORICAL_AUTHORITY_PRESENTATION_FAILURE_CONTAINMENT_IMPLEMENTED',

      auditMilestone:
        'CC-TREE-ARCH-075',

      guardedProjectionResolverImplemented:
        true,

      deterministicValidationImplemented:
        true,

      immutableNeutralFallbackImplemented:
        true,

      localReactErrorBoundaryImplemented:
        true,

      workspaceSiblingSurfacesPreserved:
        true,

      commandCenterSafePanelReused:
        false,

      runtimeDecisionDependencyAdded:
        false,

      runtimeGuardDependencyAdded:
        false,

      operationalCertificationDependencyAdded:
        false,

      memberFacingStateAdded:
        false,

      certifiesTreeHealth:
        false,

      certifiesReleaseReadiness:
        false,

      certifiesDeploymentReadiness:
        false,

      containmentImplementationCompleted:
        true,

      nextGovernedDependency:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FAILURE_CONTAINMENT_CERTIFICATION',
    });
